import { PropsWithChildren, createContext, useContext, useState } from 'react';

import mainPalette from 'assets/palettes/main';
import { ExtractedPalette } from 'types/m3';
import { IterableObject } from 'types/types';
import { getFromLocalStorage } from 'utils/local-storage';
import { parseURL } from 'utils/url';
import { cleanPaletteSource, extractTokenToPalette } from 'utils/colors';

/**
 * State that we can mutate
 */
type AppInitialState = {
  isDarkMode: boolean;
  paletteSource: string;
  palettes: IterableObject<ExtractedPalette> & {
    main: ExtractedPalette;
  };
};
/**
 * Reducers that mutate the state
 */
type AppReducers = {
  setIsDarkMode: (isDarkMode: boolean) => boolean | void;
  updatePalette: (palette?: string | null) => void;
};
/**
 * Single store
 */
type AppStore = AppInitialState & AppReducers;

const localUserColorScheme = getFromLocalStorage('user_color_scheme');
const parsedURL = parseURL(window.location.search);

/**
 * Initial state / store
 */
const initialStore: AppStore = {
  isDarkMode:
    typeof parsedURL.is_dark_mode !== 'undefined'
      ? parsedURL.is_dark_mode
      : getFromLocalStorage('is-dark-mode') ?? false,
  paletteSource: cleanPaletteSource(localUserColorScheme || mainPalette),
  palettes: {
    main: extractTokenToPalette(
      cleanPaletteSource(localUserColorScheme || mainPalette),
    ),
  },
  updatePalette: (palette?: string | null) => {
    throw new Error('Implementation required');
  },
  setIsDarkMode: (isDarkMode: boolean) => {
    throw new Error('Implementation required');
  },
};

/**
 * Context Instance
 */
const AppContext = createContext<AppStore>(initialStore);

export const useAppProvider = (): AppStore => useContext(AppContext);

export const AppProvider = ({ children }: PropsWithChildren) => {
  const [state, setState] = useState<AppStore>(initialStore);

  /**
   * Define all the handlers here how you want to mutate the state
   */
  const updatePalette = (palette?: string | null) => {
    const source = cleanPaletteSource(palette || mainPalette);

    setState((state) => ({
      ...state,
      paletteSource: source,
      palettes: {
        ...state.palettes,
        main: extractTokenToPalette(source),
      },
    }));
  };

  const setIsDarkMode = (isDarkMode: boolean) => {
    setState((state) => ({ ...state, isDarkMode }));
  };

  return (
    <AppContext.Provider
      value={{
        ...state,
        updatePalette,
        setIsDarkMode,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};
